import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Stack,
  Center,
  Input,
  FormControl,
  RadioGroup,
  HStack,
} from '@chakra-ui/react';
import { each, filter, find, includes, isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { QueryStatus } from 'services/types';

import { NotificationService } from 'api/services';
import { Tag } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { Select } from 'components/DataEntry';
import { FormLabel } from 'components/DataEntry/Form/fields/FormLabel';
import { defaultStyles as defaultFormStyles } from 'components/DataEntry/Form/styles';
import { selectAlertWorkflow } from 'containers/PolicyHub/AlertWorkflow/selectors';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { radioBoxStyles } from 'containers/Setup/CloudAccounts/CloudAccountWizard/styles';
import { toTitleCase } from 'utils/string';

import { actions } from '../../../../slice';

interface Props {
  clientInfo: any;
}

export const JiraComponent: FC<Props> = props => {
  const { clientInfo } = props;
  const dispatch = useDispatch();
  const {
    jiraMandatoryFields,
    jiraClients,
    clientFieldOptions,
    clientLabels,
  } = useSelector(selectAlertWorkflow);
  const [boardOptions, setBoardOptions] = useState<Record<string, any>[]>([]);
  const [labelOptions, setLabelOptions] = useState<Record<string, any>[]>([]);
  const { setLearnMore } = useKnowledgeBase();
  const [showBoardOptions, setShowBoardOptions] = useState(true);

  //set client options
  useEffect(() => {
    const payload = { clientId: clientInfo?.uuid, name: clientInfo?.name };
    const fieldOptionsPayload = {
      clientId: clientInfo?.uuid,
      fieldName: 'boards',
    };
    dispatch(
      actions.getMandatoryJiraFields({
        q: payload,
        status: QueryStatus.pending,
      }),
    );
    dispatch(
      actions.getClientLabels({
        q: { clientId: clientInfo?.uuid },
        status: QueryStatus.pending,
      }),
    );
    dispatch(
      actions.getClientFieldOptions({
        q: fieldOptionsPayload,
        status: QueryStatus.pending,
      }),
    );

    NotificationService.Clients.getJiraProjectDetails(payload).then(res => {
      dispatch(
        actions.getMandatoryJiraFields({
          q: payload,
          status: QueryStatus.fulfilled,
          data: res.data,
        }),
      );
    });
    NotificationService.Clients.getClientLabels(payload).then(res => {
      dispatch(
        actions.getClientLabels({
          q: { clientId: clientInfo?.uuid },
          status: QueryStatus.fulfilled,
          data: res.data,
        }),
      );
    });
    NotificationService.Clients.getFieldOptions(fieldOptionsPayload).then(
      res => {
        dispatch(
          actions.getClientFieldOptions({
            q: fieldOptionsPayload,
            status: QueryStatus.fulfilled,
            data: res.data,
          }),
        );
      },
    );
  }, [dispatch, clientInfo]);

  useEffect(() => {
    let options: Record<string, any>[] = map(
      clientFieldOptions?.[clientInfo.uuid]?.data,
      o => ({
        label: o.name,
        value: o.id,
        data: o,
      }),
    );
    setBoardOptions(options);
  }, [clientFieldOptions?.[clientInfo.uuid]?.data, clientInfo.uuid]);

  useEffect(() => {
    let options: Record<string, any>[] = map(
      clientLabels?.[clientInfo.uuid]?.data,
      o => ({
        label: o,
        value: o,
      }),
    );
    setLabelOptions(options);
  }, [clientLabels?.[clientInfo.uuid]?.data, clientInfo.uuid]);

  useEffect(() => {
    if (!!jiraClients?.[clientInfo.name]?.board_id) {
      setShowBoardOptions(true);
    } else {
      setShowBoardOptions(false);
    }
  }, [jiraClients?.[clientInfo.name]?.board_id]);

  const content = (name, value) => (
    <Stack direction="row" align="center" w="full">
      <Box fontSize="sm">{name}</Box>
      <Tag label={value} />
    </Stack>
  );

  const getOptions = list =>
    map(list, o => ({
      label: o?.name ?? o.value,
      value: { id: o.id, value: o?.name ?? o.value },
    }));

  const updateFields = (field, value) => {
    dispatch(
      actions.updateJiraClientFields({ field, value, name: clientInfo.name }),
    );
  };

  const removeField = field => {
    const filteredFields: Record<string, any> = {};
    each(jiraClients?.[clientInfo.name], (v, k) => {
      if (k !== field) {
        filteredFields[k] = v;
      }
    });
    dispatch(
      actions.updateJiraClient({
        fields: filteredFields,
        name: clientInfo.name,
      }),
    );
  };

  return (
    <Box border="1px solid" borderColor="gray.200" p={2} borderRadius={6}>
      <Stack spacing={4}>
        <Stack direction="row">
          {content('Name', clientInfo?.name)}
          {content(
            'Project Name',
            clientInfo?.jira?.project_name || clientInfo?.jira?.project_id,
          )}
        </Stack>
        {jiraMandatoryFields?.[clientInfo.uuid]?.isLoading && (
          <Center py={2}>
            <CustomSpinner
              loading={jiraMandatoryFields?.[clientInfo.uuid]?.isLoading}
            />
          </Center>
        )}
        {!jiraMandatoryFields?.[clientInfo.uuid]?.isLoading &&
          map(jiraMandatoryFields?.[clientInfo.uuid]?.data, o => {
            const options = getOptions(o?.allowed_values);
            const all = jiraClients?.[clientInfo.name]?.[o?.name];

            const selected = isEmpty(o)
              ? all?.[0] ?? ''
              : /*: isEmpty(all)
              ? options?.[0] ?? (o.type === 'array' ? [] : {})*/
              o.type === 'array'
              ? filter(options, op =>
                  includes(
                    map(all, k => k?.id),
                    op.value?.id,
                  ),
                )
              : find(options, op => op.value?.id === all?.id);
            //console.log(selected, all, isEmpty(all), o, options);
            return (
              <FormControl isRequired={true} key={'jira' + o?.name}>
                <FormLabel
                  htmlFor={o?.name}
                  label={toTitleCase(o?.name)}
                  isRequired={true}
                  styles={{
                    ...defaultFormStyles.textField?.label,
                    color: '#000',
                  }}
                />
                {isEmpty(o) ? (
                  <Input
                    {...defaultFormStyles.textField?.input}
                    value={selected}
                    onChange={e => updateFields(o?.name, [e.target.value])}
                  />
                ) : (
                  <Select
                    options={options}
                    isMulti={o.type === 'array'}
                    value={selected}
                    onChange={selected => {
                      updateFields(
                        o?.name,
                        o.type === 'array'
                          ? map(selected, l => l.value)
                          : selected?.value,
                      );
                    }}
                  />
                )}
              </FormControl>
            );
          })}
        <FormControl>
          <FormLabel
            htmlFor="summary-prefix"
            label="Summary Prefix"
            styles={{
              ...defaultFormStyles.textField?.label,
              color: '#000',
            }}
            helpLabel="What’s this?"
            onClickHelpIcon={() => setLearnMore('jira_project_summary_prefix')}
          />
          <Input
            {...defaultFormStyles.textField?.input}
            value={jiraClients?.[clientInfo.name]?.ticket_summary_prefix}
            onChange={e =>
              updateFields('ticket_summary_prefix', e.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel
            htmlFor="Labels For Ticket"
            label="Labels For Ticket"
            styles={{
              ...defaultFormStyles.textField?.label,
              color: '#000',
            }}
          />
          <Select
            options={labelOptions}
            isMulti
            value={map(jiraClients?.[clientInfo.name]?.ticket_labels, o => ({
              label: o,
              value: o,
            }))}
            creatable
            onCreateOption={label => {
              setLabelOptions(prev => [...prev, { label, value: label }]);
              updateFields('ticket_labels', [
                ...jiraClients?.[clientInfo.name]?.ticket_labels,
                label,
              ]);
            }}
            isLoading={clientLabels?.[clientInfo.uuid]?.isLoading}
            onChange={selected => {
              updateFields(
                'ticket_labels',
                selected?.map(l => l.value),
              );
            }}
          />
        </FormControl>
        <FormControl>
          <FormLabel
            htmlFor="Sprint"
            label="Assign to Sprint"
            styles={{
              ...defaultFormStyles.textField?.label,
              color: '#000',
            }}
          />
          <HStack {...radioBoxStyles}>
            <RadioGroup
              value={showBoardOptions ? 'Current' : 'Any'}
              onChange={value => {
                setShowBoardOptions(value === 'Current');
                if (value === 'Any') removeField('board_id');
              }}
            >
              <HStack spacing={10}>
                {renderRadioButton(
                  'Current sprint',
                  `Assign ticket to board's current sprint`,
                  'Current',
                )}
                {renderRadioButton(
                  'No sprint',
                  'You can choose sprint on JIRA board',
                  'Any',
                )}
              </HStack>
            </RadioGroup>
          </HStack>
        </FormControl>
        {showBoardOptions && (
          <FormControl>
            <FormLabel
              htmlFor="Board"
              label="Board"
              styles={{
                ...defaultFormStyles.textField?.label,
                color: '#000',
              }}
            />
            <Select
              options={boardOptions}
              value={find(
                boardOptions,
                o => o.value === jiraClients?.[clientInfo.name]?.board_id,
              )}
              isLoading={clientFieldOptions?.[clientInfo.uuid]?.isLoading}
              onChange={selected => {
                updateFields('board_id', selected?.value);
              }}
            />
          </FormControl>
        )}
      </Stack>
      <LearnMore />
    </Box>
  );
};
